import React, { useState, useEffect, useRef } from 'react';
import {
    Plus, Edit2, Trash2, Building, Users, ChevronDown, ChevronRight, X, Check, Maximize2, Minimize2, Minus
} from 'lucide-react';
import { useEmployees } from '../../../contexts/EmployeeContext';
import functionalStructureService from '../../../services/functionalStructure.service';

const FunctionalStructure = ({ readOnly = false }) => {
    const { employees } = useEmployees();
    const [legalEntities, setLegalEntities] = useState([]);
    const [editingCard, setEditingCard] = useState(null);
    const [editValue, setEditValue] = useState('');
    const [expandedEntities, setExpandedEntities] = useState({});
    const [detailedView, setDetailedView] = useState(false);
    const [zoomLevel, setZoomLevel] = useState(1);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const fullscreenRef = useRef(null);
    const chartContainerRef = useRef(null);

    const [loading, setLoading] = useState(false);
    const [isInitialized, setIsInitialized] = useState(false);

    // Fetch from backend on mount
    useEffect(() => {
        fetchFunctionalStructure();
    }, []);

    const fetchFunctionalStructure = async () => {
        const authToken = localStorage.getItem('authToken');

        // Load from localStorage first for immediate display
        const stored = localStorage.getItem('functionalStructure');
        if (stored) {
            try {
                const parsed = JSON.parse(stored);
                setLegalEntities(parsed.legalEntities || []);
                setExpandedEntities(parsed.expandedEntities || {});
            } catch (e) {
                console.error('Error loading from localStorage:', e);
            }
        }

        // Then fetch from backend if authenticated
        if (!authToken) {
            //console.log('⚠️ No auth token, using localStorage functional structure');
            return;
        }

        try {
            setLoading(true);
            const data = await functionalStructureService.getActive();

            if (data && data.structure_data) {
                const structureData = data.structure_data;
                setLegalEntities(structureData.legalEntities || []);
                setExpandedEntities(structureData.expandedEntities || {});

                // Save to localStorage for offline access
                functionalStructureService.saveToLocalStorage({
                    legalEntities: structureData.legalEntities || [],
                    expandedEntities: structureData.expandedEntities || {}
                });
                setIsInitialized(true);
            }
        } catch (error) {
            console.error('Error fetching functional structure, using localStorage:', error);
        } finally {
            setLoading(false);
        }
    };

    // Save to backend whenever structure changes (after initialization)
    useEffect(() => {
        if (isInitialized) {
            saveFunctionalStructure();
        }
    }, [legalEntities, expandedEntities, isInitialized]);

    const saveFunctionalStructure = async () => {
        const authToken = localStorage.getItem('authToken');

        // Always save to localStorage for immediate access
        const structureData = {
            legalEntities,
            expandedEntities
        };
        functionalStructureService.saveToLocalStorage(structureData);

        // Save to backend if authenticated
        if (authToken) {
            try {
                await functionalStructureService.upsert(structureData);
            } catch (error) {
                console.error('Error saving functional structure to backend:', error);
            }
        }
    };

    // Calculate employees in each legal entity
    const getEntityEmployeeCount = (entityId) => {
        const entity = legalEntities.find(e => e.id === entityId);
        if (!entity) return 0;

        let count = 0;
        entity.departments.forEach(dept => {
            count += dept.employees || 0;
        });
        return count;
    };

    // Calculate total employees in all legal entities
    const getTotalLegalEntityEmployees = () => {
        return legalEntities.reduce((sum, entity) => sum + getEntityEmployeeCount(entity.id), 0);
    };

    const handleAddLegalEntity = () => {
        const newEntity = {
            id: `entity-${Date.now()}`,
            name: 'Add Legal Entity',
            isNew: true,
            departments: []
        };
        setLegalEntities([...legalEntities, newEntity]);
        setEditingCard(newEntity.id);
        setEditValue('');
    };

    const handleEditCard = (id, currentName) => {
        setEditingCard(id);
        setEditValue(currentName === 'Add Legal Entity' || currentName === 'Add Department' ? '' : currentName);
    };

    const handleSaveCard = (id, type, parentId = null) => {
        if (type === 'legalEntity') {
            setLegalEntities(prev => prev.map(entity =>
                entity.id === id
                    ? { ...entity, name: editValue || 'Unnamed Legal Entity', isNew: false }
                    : entity
            ));
        } else if (type === 'department') {
            // Ensure parent entity remains expanded when saving department
            setExpandedEntities(prev => ({
                ...prev,
                [parentId]: true
            }));

            setLegalEntities(prev => prev.map(entity => {
                if (entity.id === parentId) {
                    return {
                        ...entity,
                        departments: entity.departments.map(dept =>
                            dept.id === id
                                ? { ...dept, name: editValue || 'Unnamed Department', isNew: false }
                                : dept
                        )
                    };
                }
                return entity;
            }));
        }
        setEditingCard(null);
        setEditValue('');
    };

    const handleDeleteCard = (id, type, parentId = null) => {
        if (type === 'legalEntity') {
            setLegalEntities(prev => prev.filter(entity => entity.id !== id));
        } else if (type === 'department') {
            setLegalEntities(prev => prev.map(entity => {
                if (entity.id === parentId) {
                    return {
                        ...entity,
                        departments: entity.departments.filter(dept => dept.id !== id)
                    };
                }
                return entity;
            }));
        }
    };

    const handleAddDepartment = (entityId) => {
        const newDept = {
            id: `dept-${Date.now()}`,
            name: 'Add Department',
            isNew: true,
            employees: 0
        };

        // Ensure entity is expanded
        setExpandedEntities(prev => ({
            ...prev,
            [entityId]: true
        }));

        setLegalEntities(prev => prev.map(entity =>
            entity.id === entityId
                ? { ...entity, departments: [...entity.departments, newDept] }
                : entity
        ));
        setEditingCard(newDept.id);
        setEditValue('');
    };

    const toggleEntityExpanded = (entityId) => {
        setExpandedEntities(prev => ({
            ...prev,
            [entityId]: !prev[entityId]
        }));
    };

    // Calculate the actual width of a legal entity card including its expanded departments
    const getEntityCardWidth = (entity) => {
        if (expandedEntities[entity.id] && entity.departments.length > 0) {
            // When expanded, width = total width of all department cards + gaps + add button (if not readOnly)
            const deptCount = readOnly ? entity.departments.length : entity.departments.length + 1; // +1 for Add button only if not readOnly
            return deptCount * 280 - 24; // 256px card + 24px gap, minus last gap
        }
        return 288; // Default collapsed card width
    };

    // Get the center position of each legal entity card
    const getLegalEntityCenterPositions = () => {
        const positions = [];
        let currentPos = 0;
        legalEntities.forEach((entity) => {
            const cardWidth = getEntityCardWidth(entity);
            positions.push(currentPos + cardWidth / 2);
            currentPos += cardWidth + 24; // card width + gap
        });
        // Add button position - only if not read-only
        if (!readOnly) {
            positions.push(currentPos + 144); // 288/2 for Add button center
        }
        return positions;
    };

    // Calculate horizontal line width from first to last vertical line
    const getTotalLegalEntitiesWidth = () => {
        const positions = getLegalEntityCenterPositions();
        if (positions.length < 2) return 0;
        // Distance from first vertical line to last vertical line
        return positions[positions.length - 1] - positions[0];
    };

    // Get department center positions for a specific entity
    const getDepartmentCenterPositions = (entity) => {
        const positions = [];
        const deptCount = entity.departments.length;
        let currentPos = 0;

        entity.departments.forEach(() => {
            positions.push(currentPos + 128); // 256/2 for department card center
            currentPos += 280; // 256 card + 24 gap
        });
        // Add button position - only if not read-only
        if (!readOnly) {
            positions.push(currentPos + 128); // 256/2 for Add button center
        }
        return positions;
    };

    // Get total width for departments horizontal line (first to last vertical)
    const getDepartmentsTotalWidth = (entity) => {
        const positions = getDepartmentCenterPositions(entity);
        if (positions.length < 2) return 0;
        // Distance from first vertical line to last vertical line
        return positions[positions.length - 1] - positions[0];
    };

    // Handle detailed view toggle - expand all entities
    useEffect(() => {
        if (detailedView) {
            const allExpanded = {};
            legalEntities.forEach(entity => {
                if (!entity.isNew) {
                    allExpanded[entity.id] = true;
                }
            });
            setExpandedEntities(allExpanded);
        } else {
            setExpandedEntities({});
        }
    }, [detailedView]); // Removed legalEntities to prevent auto-collapse when editing departments

    const handleZoomIn = () => {
        setZoomLevel(prev => Math.min(prev + 0.1, 2));
    };

    const handleZoomOut = () => {
        setZoomLevel(prev => Math.max(prev - 0.1, 0.5));
    };

    const handleFullscreen = () => {
        if (!isFullscreen) {
            if (fullscreenRef.current?.requestFullscreen) {
                fullscreenRef.current.requestFullscreen();
            }
        } else {
            if (document.exitFullscreen) {
                document.exitFullscreen();
            }
        }
        setIsFullscreen(!isFullscreen);
    };

    const chartScrollStyle = {
        overflowX: 'auto',
        overflowY: 'auto',
        height: isFullscreen ? '100vh' : 'calc(100vh - 200px)',
        position: 'relative'
    };

    return (
        <div ref={fullscreenRef} className={`min-h-screen bg-gray-50 dark:bg-gray-900 ${isFullscreen ? 'fixed inset-0 z-50' : ''}`}>
            {/* Header with Controls */}
            {!isFullscreen && (
                <div className="bg-white dark:bg-gray-800 px-6 py-3 border-b border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-between">
                        <div>
                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                                Functional Structure
                            </h2>
                            {!readOnly && (
                                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                                    Build your organizational hierarchy with legal entities and departments
                                </p>
                            )}
                            {readOnly && (
                                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                                    View organizational hierarchy with legal entities and departments
                                </p>
                            )}
                        </div>

                        <div className="flex items-center space-x-4">
                            <div className="flex items-center space-x-2">
                                <span className="text-sm text-gray-600 dark:text-gray-400">Detailed View</span>
                                <button
                                    onClick={() => setDetailedView(!detailedView)}
                                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                                        detailedView ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
                                    }`}
                                >
                                    <span
                                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                            detailedView ? 'translate-x-6' : 'translate-x-1'
                                        }`}
                                    />
                                </button>
                            </div>

                            <div className="flex items-center space-x-1 border-l border-gray-300 dark:border-gray-600 pl-4">
                                <button
                                    onClick={handleZoomOut}
                                    className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                                    title="Zoom Out"
                                >
                                    <Minus className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                                </button>
                                <button
                                    onClick={handleZoomIn}
                                    className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                                    title="Zoom In"
                                >
                                    <Plus className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                                </button>
                                <button
                                    onClick={handleFullscreen}
                                    className={`p-2 rounded transition-colors ${
                                        isFullscreen ? 'bg-blue-500 text-white' : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                                    }`}
                                    title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}
                                >
                                    {isFullscreen ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5 text-gray-600 dark:text-gray-400" />}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Fullscreen Exit Button */}
            {isFullscreen && (
                <div className="absolute top-4 right-4 z-50">
                    <button
                        onClick={handleFullscreen}
                        className="p-2 bg-blue-500 text-white rounded-lg shadow-lg hover:bg-blue-600"
                        title="Exit Fullscreen"
                    >
                        <Minimize2 className="w-5 h-5" />
                    </button>
                </div>
            )}

            {/* Hierarchy Structure */}
            <div ref={chartContainerRef} className="p-6 overflow-visible" style={chartScrollStyle}>
                <div className="inline-block" style={{
                    transform: `scale(${zoomLevel})`,
                    transformOrigin: 'top center',
                    minWidth: '100%'
                }}>
                    <div className="flex flex-col items-center space-y-6">
                {/* Our Organization - Top Level (Non-editable) */}
                <div className="w-full max-w-md">
                    <div className="bg-gradient-to-br from-blue-600 to-blue-700 text-white rounded-xl shadow-lg p-6 text-center">
                        <Building className="w-12 h-12 mx-auto mb-3" />
                        <h3 className="text-2xl font-bold mb-2">Our Organization</h3>
                        <div className="flex items-center justify-center gap-2">
                            <Users className="w-5 h-5" />
                            <span className="text-lg font-semibold">
                                {getTotalLegalEntityEmployees()} Employees
                            </span>
                        </div>
                    </div>
                </div>

                {/* Legal Entities Level - Horizontal Layout */}
                {legalEntities.length > 0 && (() => {
                    const centerPositions = getLegalEntityCenterPositions();
                    const totalWidth = getTotalLegalEntitiesWidth();
                    const firstPos = centerPositions[0];

                    return (
                    <div className="w-full flex flex-col items-center">
                        {/* Vertical connector from Our Organization */}
                        <div className="h-12 w-1 bg-gray-300 dark:bg-gray-600"></div>

                        {/* Container for horizontal line and cards */}
                        <div className="relative flex flex-col items-center">
                            {/* Horizontal connector line - spans from first to last vertical line */}
                            <div
                                className="absolute h-1 bg-gray-300 dark:bg-gray-600"
                                style={{
                                    top: 0,
                                    left: `${firstPos}px`,
                                    width: `${totalWidth}px`
                                }}
                            ></div>

                            {/* Vertical lines going down to center of each card */}
                            {centerPositions.map((position, index) => (
                                <div
                                    key={index}
                                    className="absolute h-12 w-1 bg-gray-300 dark:bg-gray-600"
                                    style={{
                                        top: 0,
                                        left: `${position}px`,
                                        transform: 'translateX(-50%)'
                                    }}
                                ></div>
                            ))}

                        <div className="flex items-start justify-center gap-6 pt-12">
                            {legalEntities.map((entity, index) => (
                            <div key={entity.id} className="flex flex-col items-center" style={{ minWidth: '288px' }}>
                                {/* Legal Entity Card */}
                                <div className={`tour-legal-entity-card ${!entity.isNew ? 'tour-legal-entity-existing-card' : ''} w-72 bg-white dark:bg-gray-800 rounded-xl shadow-md border-2 ${
                                    entity.isNew ? 'border-dashed border-blue-400' : 'border-gray-200 dark:border-gray-700'
                                } p-5`}>
                                    <div className="flex items-start justify-between mb-3">
                                        <Building className="w-8 h-8 text-blue-600" />
                                        {!readOnly && (
                                            <div className="flex gap-2">
                                                {editingCard !== entity.id && (
                                                    <>
                                                        <button
                                                            onClick={() => handleEditCard(entity.id, entity.name)}
                                                            className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                                                            title="Edit Legal Entity"
                                                        >
                                                            <Edit2 className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                                                        </button>
                                                        <button
                                                            onClick={() => handleDeleteCard(entity.id, 'legalEntity')}
                                                            className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                                                            title="Delete Legal Entity"
                                                        >
                                                            <Trash2 className="w-4 h-4 text-red-600" />
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    {editingCard === entity.id ? (
                                        <div className="space-y-3">
                                            <input
                                                type="text"
                                                value={editValue}
                                                onChange={(e) => setEditValue(e.target.value)}
                                                placeholder="Enter legal entity name"
                                                className="tour-legal-entity-input w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                                autoFocus
                                            />
                                            <div className="flex gap-2">
                                                <button
                                                    onClick={() => handleSaveCard(entity.id, 'legalEntity')}
                                                    className="tour-save-legal-entity flex-1 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center justify-center gap-2"
                                                >
                                                    <Check className="w-4 h-4" />
                                                    Save
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        setEditingCard(null);
                                                        setEditValue('');
                                                        if (entity.isNew) {
                                                            handleDeleteCard(entity.id, 'legalEntity');
                                                        }
                                                    }}
                                                    className="px-3 py-2 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-white rounded-lg hover:bg-gray-400"
                                                >
                                                    <X className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        <>
                                            <h4 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                                                {entity.name}
                                            </h4>
                                            <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400 mb-3">
                                                <Users className="w-4 h-4" />
                                                <span className="text-sm font-medium">
                                                    {getEntityEmployeeCount(entity.id)} Employees
                                                </span>
                                            </div>

                                            {!entity.isNew && (
                                                <>
                                                    <button
                                                        onClick={() => toggleEntityExpanded(entity.id)}
                                                        className="w-full flex items-center justify-between px-3 py-2 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300"
                                                    >
                                                        <span className="text-sm font-medium">
                                                            {entity.departments.length} Departments
                                                        </span>
                                                        {expandedEntities[entity.id] ? (
                                                            <ChevronDown className="w-4 h-4" />
                                                        ) : (
                                                            <ChevronRight className="w-4 h-4" />
                                                        )}
                                                    </button>
                                                </>
                                            )}
                                        </>
                                    )}
                                </div>

                                {/* Departments under this Legal Entity - Horizontal */}
                                {expandedEntities[entity.id] && !entity.isNew && (() => {
                                    const deptCenterPositions = getDepartmentCenterPositions(entity);
                                    const deptTotalWidth = getDepartmentsTotalWidth(entity);
                                    const deptFirstPos = deptCenterPositions[0];

                                    return (
                                    <div className="flex flex-col items-center w-full">
                                        {/* Vertical connector from Legal Entity */}
                                        <div className="h-12 w-1 bg-gray-300 dark:bg-gray-600"></div>

                                        {/* Container for horizontal line and department cards */}
                                        <div className="relative flex flex-col items-center">
                                            {/* Horizontal connector line - spans from first to last vertical line */}
                                            <div
                                                className="absolute h-1 bg-gray-300 dark:bg-gray-600"
                                                style={{
                                                    top: 0,
                                                    left: `${deptFirstPos}px`,
                                                    width: `${deptTotalWidth}px`
                                                }}
                                            ></div>

                                            {/* Vertical lines going down to center of each department card */}
                                            {deptCenterPositions.map((position, deptIndex) => (
                                                <div
                                                    key={deptIndex}
                                                    className="absolute h-12 w-1 bg-gray-300 dark:bg-gray-600"
                                                    style={{
                                                        top: 0,
                                                        left: `${position}px`,
                                                        transform: 'translateX(-50%)'
                                                    }}
                                                ></div>
                                            ))}

                                        <div className="flex items-start gap-6 pt-12">
                                            {entity.departments.map((dept, deptIndex) => (
                                                <div key={dept.id} className="flex flex-col items-center">
                                                    <div
                                                        className={`w-64 bg-white dark:bg-gray-800 rounded-lg shadow border ${
                                                            dept.isNew ? 'border-dashed border-green-400' : 'border-gray-200 dark:border-gray-700'
                                                        } p-4`}
                                                    >
                                                        <div className="flex items-start justify-between">
                                                            <div className="flex-1">
                                                                {editingCard === dept.id ? (
                                                                    <div className="space-y-3">
                                                                        <input
                                                                            type="text"
                                                                            value={editValue}
                                                                            onChange={(e) => setEditValue(e.target.value)}
                                                                            placeholder="Enter department name"
                                                                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                                                            autoFocus
                                                                        />
                                                                        <div className="flex gap-2">
                                                                            <button
                                                                                onClick={() => handleSaveCard(dept.id, 'department', entity.id)}
                                                                                className="flex-1 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center justify-center gap-2"
                                                                            >
                                                                                <Check className="w-4 h-4" />
                                                                                Save
                                                                            </button>
                                                                            <button
                                                                                onClick={() => {
                                                                                    setEditingCard(null);
                                                                                    setEditValue('');
                                                                                    if (dept.isNew) {
                                                                                        handleDeleteCard(dept.id, 'department', entity.id);
                                                                                    }
                                                                                }}
                                                                                className="px-3 py-2 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-white rounded-lg hover:bg-gray-400"
                                                                            >
                                                                                <X className="w-4 h-4" />
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                ) : (
                                                                    <>
                                                                        <h5 className="text-md font-semibold text-gray-900 dark:text-white mb-1">
                                                                            {dept.name}
                                                                        </h5>
                                                                        <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                                                                            <Users className="w-3 h-3" />
                                                                            <span className="text-xs">
                                                                                {dept.employees} Employees
                                                                            </span>
                                                                        </div>
                                                                    </>
                                                                )}
                                                            </div>

                                                            {!readOnly && editingCard !== dept.id && (
                                                                <div className="flex gap-2 ml-2">
                                                                    <button
                                                                        onClick={() => handleEditCard(dept.id, dept.name)}
                                                                        className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                                                                        title="Edit Department"
                                                                    >
                                                                        <Edit2 className="w-3 h-3 text-gray-600 dark:text-gray-400" />
                                                                    </button>
                                                                    <button
                                                                        onClick={() => handleDeleteCard(dept.id, 'department', entity.id)}
                                                                        className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                                                                        title="Delete Department"
                                                                    >
                                                                        <Trash2 className="w-3 h-3 text-red-600" />
                                                                    </button>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}

                                            {/* Add Department Button - Horizontal */}
                                            {!readOnly && (
                                                <button
                                                    onClick={() => handleAddDepartment(entity.id)}
                                                    className="w-64 h-24 border-2 border-dashed border-blue-400 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 text-blue-600 dark:text-blue-400 flex flex-col items-center justify-center gap-2"
                                                >
                                                    <Plus className="w-5 h-5" />
                                                    <span className="text-sm font-medium">Add Department</span>
                                                </button>
                                            )}
                                        </div>
                                        </div>
                                    </div>
                                    );
                                })()}
                            </div>
                        ))}

                            {/* Add Legal Entity Button - Next to existing cards */}
                            {!readOnly && (
                                <div className="flex flex-col items-center">
                                    <button
                                        onClick={handleAddLegalEntity}
                                        className="tour-add-legal-entity w-72 h-48 border-2 border-dashed border-blue-400 rounded-xl hover:bg-blue-50 dark:hover:bg-blue-900/20 text-blue-600 dark:text-blue-400 flex flex-col items-center justify-center gap-3 transition-all hover:shadow-lg"
                                    >
                                        <Plus className="w-8 h-8" />
                                        <span className="text-lg font-semibold">Add Legal Entity</span>
                                    </button>
                                </div>
                            )}
                        </div>
                        </div>
                    </div>
                    );
                })()}

                {/* Initial Add Legal Entity Button - When no entities exist */}
                {legalEntities.length === 0 && !readOnly && (
                    <button
                        onClick={handleAddLegalEntity}
                        className="tour-add-legal-entity px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium flex items-center gap-2 shadow-lg transition-all hover:shadow-xl"
                    >
                        <Plus className="w-5 h-5" />
                        Add Legal Entity
                    </button>
                )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FunctionalStructure;
